/**
 * The enricher's view of the current Character and of the Entry being read: check statistics by
 * Foundry's inline type, `@actor.*` / `@self.*` / `@item.*` roll data, and the Level. Built from
 * the derived sheet (not the engine state), so it carries what the sheet shows: Overrides included.
 */
import type { Entry } from "../../shared/snapshot-format";
import type { Character } from "../character";
import type { DerivedSheet, StatisticView } from "../engine";
import { SAVES } from "../engine/values";
import type { ActorContext, CheckStatistic, ItemContext } from "./enrich";

function statisticOf(view: StatisticView): CheckStatistic {
  return { slug: view.slug, label: view.label, modifier: view.value };
}

function loreLabel(slug: string): string {
  return slug
    .split("-")
    .map((w) => w.slice(0, 1).toUpperCase() + w.slice(1))
    .join(" ");
}

/** The Character's numbers for inline checks and damage. */
export function actorContext(sheet: DerivedSheet, character: Pick<Character, "variantRules">): ActorContext {
  const spellDCs = sheet.spellcasting.map((e) => e.statistic?.dc ?? 0);
  const classOrSpellDC = Math.max(0, sheet.classDC?.dc ?? 0, ...spellDCs);
  const statistic = (rawType: string): CheckStatistic | null => {
    const type = rawType.toLowerCase().replace(/\s+/g, "-");
    const skill = sheet.skills[type];
    if (skill) return statisticOf(skill);
    if ((SAVES as readonly string[]).includes(type)) return statisticOf(sheet.saves[type as (typeof SAVES)[number]]);
    if (type === "perception") return statisticOf(sheet.perception);
    if (type === "initiative") return statisticOf(sheet.initiative);
    const special = sheet.specialStatistics[type];
    if (special) return statisticOf(special);
    const classDC = sheet.classDCs[type];
    if (classDC) return statisticOf(classDC);
    if (type === "class" && sheet.classDC) return statisticOf(sheet.classDC);
    if (type === "lore" || type.endsWith("-lore")) {
      // An untrained Lore: the Intelligence modifier alone (Player Core), whatever the Level.
      return { slug: type, label: loreLabel(type), modifier: sheet.abilities.int.value };
    }
    return null;
  };
  const lookup = (path: string): unknown => {
    const p = path.replace(/^self\./, "actor.").replace(/^actor\.system\./, "actor.");
    if (p === "actor.level") return sheet.level;
    let m = /^actor\.abilities\.(\w+)\.mod$/.exec(p);
    if (m) return sheet.abilities[m[1] as keyof DerivedSheet["abilities"]]?.value;
    m = /^actor\.skills\.([\w-]+)\.(rank|mod|value)$/.exec(p);
    if (m) {
      const skill = sheet.skills[m[1]!];
      if (!skill) return m[2] === "rank" ? 0 : undefined;
      return m[2] === "rank" ? (skill.rank ?? 0) : skill.value;
    }
    m = /^actor\.saves\.(\w+)\.(mod|value)$/.exec(p);
    if (m) return sheet.saves[m[1] as (typeof SAVES)[number]]?.value;
    if (p === "actor.attributes.classOrSpellDC.value") return classOrSpellDC;
    if (p === "actor.attributes.classDC.value") return sheet.classDC?.dc ?? 0;
    if (p === "actor.attributes.perception.value" || p === "actor.perception.mod") return sheet.perception.value;
    if (p === "actor.attributes.ac.value") return sheet.ac.dc;
    if (p === "actor.attributes.hp.max") return sheet.hp.value;
    if (p === "actor.resources.focus.max") return sheet.resources.focus.max;
    if (p === "actor.resources.heroPoints.max") return sheet.resources.heroPoints.max;
    if (p === "actor.attributes.speed.value" || p === "actor.speeds.land.value") return sheet.speeds.land.value;
    return undefined;
  };
  return { level: sheet.level, proficiencyWithoutLevel: character.variantRules.proficiencyWithoutLevel, statistic, lookup };
}

function getPath(source: unknown, path: string): unknown {
  let cur: unknown = source;
  for (const key of path.split(".")) {
    if (cur === null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[key];
  }
  return cur;
}

export interface ItemContextOptions {
  /** The rank a spell is cast at, when known (a heightened spell in the Spells tab). */
  rank?: number | undefined;
  /** A Condition's value or an Effect's counter, when the Entry is in play. */
  badge?: number | undefined;
}

/** The Entry's numbers: its Level, a spell's rank, its `system` data. */
export function itemContext(entry: Entry, options: ItemContextOptions = {}): ItemContext {
  const spellRank = typeof (entry.system.level as { value?: unknown } | undefined)?.value === "number" ? ((entry.system.level as { value: number }).value) : null;
  const lookup = (path: string): unknown => {
    const p = path.replace(/^item\./, "");
    if (p === "level") return entry.level ?? spellRank ?? undefined;
    if (p === "rank") return options.rank ?? (entry.type === "spell" ? spellRank ?? undefined : undefined);
    if (p === "badge.value") return options.badge;
    if (p.startsWith("system.")) return getPath(entry.system, p.slice("system.".length));
    return getPath(entry.system, p);
  };
  return { name: entry.name, traits: entry.traits, lookup };
}
